import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireHotelLocationSuggestion, TireHotelPickupDecision } from 'noddi-types';
import { NoddiBasicCard, NoddiButton, NoddiDialog, NoddiInput } from 'noddi-ui-web';
import { useState } from 'react';

type Props = {
  suggestions: TireHotelLocationSuggestion[];
  isPending: boolean;
  /** Confirmed location, or decline with the customer-entered hotel name (forwarded to ops when booking). */
  onAnswer: (decision: TireHotelPickupDecision) => void;
  onDismiss: () => void;
};

export const TireHotelLocationSuggestionDialog = ({ suggestions, isPending, onAnswer, onDismiss }: Props) => {
  const [selectedId, setSelectedId] = useState<number | undefined>(
    suggestions.length === 1 ? suggestions[0]?.tireHotelLocationId : undefined
  );
  const [isNamingStep, setIsNamingStep] = useState(false);
  const [hotelName, setHotelName] = useState('');

  if (isNamingStep) {
    return (
      <NoddiDialog
        open
        onClose={onDismiss}
        title={t`Which tire hotel do you use?`}
        description={t`We let our team know the name so we can pick up your wheels from the right place.`}
        footer={
          <div className='flex items-center justify-end gap-2'>
            <NoddiButton variant='outline' onClick={() => setIsNamingStep(false)} disabled={isPending}>
              <Trans>Back</Trans>
            </NoddiButton>
            <NoddiButton
              variant='success'
              disabled={hotelName.trim().length === 0}
              loading={isPending}
              onClick={() =>
                onAnswer({ declinedTireHotelLocationSuggestion: true, tireHotelLocationName: hotelName.trim() })
              }
            >
              <Trans>Continue</Trans>
            </NoddiButton>
          </div>
        }
      >
        <NoddiInput
          name='tire-hotel-name'
          label={t`Name of the tire hotel`}
          placeholder={t`e.g. Mitt Dekkhotell`}
          value={hotelName}
          onChange={(e) => setHotelName(e.target.value)}
        />
      </NoddiDialog>
    );
  }

  return (
    <NoddiDialog
      open
      onClose={onDismiss}
      title={t`Is this where your tires are stored?`}
      description={t`We found a known tire hotel at the address you entered. Confirm it so we can pick up your wheels at the right time.`}
      footer={
        <div className='flex items-center justify-end gap-2'>
          <NoddiButton variant='outline' onClick={() => setIsNamingStep(true)} disabled={isPending}>
            <Trans>No, it's another tire hotel</Trans>
          </NoddiButton>
          <NoddiButton
            variant='success'
            disabled={selectedId === undefined}
            loading={isPending}
            onClick={() => {
              if (selectedId !== undefined) {
                onAnswer({ tireHotelLocationId: selectedId });
              }
            }}
          >
            <Trans>Yes, that's correct</Trans>
          </NoddiButton>
        </div>
      }
    >
      <div className='flex flex-col gap-2'>
        {suggestions.map((suggestion) => (
          <NoddiBasicCard
            key={suggestion.tireHotelLocationId}
            selected={selectedId === suggestion.tireHotelLocationId}
            onClick={() => setSelectedId(suggestion.tireHotelLocationId)}
            className='flex flex-col gap-1'
          >
            <span className='text-lg font-medium'>{suggestion.name}</span>
            <span>{suggestion.address}</span>
          </NoddiBasicCard>
        ))}
      </div>
    </NoddiDialog>
  );
};
